/*!
 * Registry and loading utilities for web components 🥊  @tko/utils.component@4.0.0-alpha8
 * (c) The Knockout.js Team - https://tko.io
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { subscribable, dependencyDetection } from '@tko/observable';
import { getObjectOwnProperty, tasks, isDomElement, isDocumentFragment, tagNameLower, parseHtmlFragment, makeArray, cloneNodes, hasOwnProperty } from '@tko/utils';
import { LifeCycle } from '@tko/lifecycle';

var loadingSubscribablesCache = {}, // Tracks component loads that are currently in flight
loadedDefinitionsCache = {}; // Tracks component loads that have already completed
function loadComponentAndNotify(componentName, callback) {
    var _subscribable = getObjectOwnProperty(loadingSubscribablesCache, componentName), completedAsync;
    if (!_subscribable) {
        // It's not started loading yet. Start loading, and when it's done, move it to loadedDefinitionsCache.
        _subscribable = loadingSubscribablesCache[componentName] = new subscribable();
        _subscribable.subscribe(callback);
        beginLoadingComponent(componentName, function (definition, config) {
            var isSynchronousComponent = !!(config && config.synchronous);
            loadedDefinitionsCache[componentName] = { definition: definition, isSynchronousComponent: isSynchronousComponent };
            delete loadingSubscribablesCache[componentName];
            // For API consistency, all loads complete asynchronously. However we want to avoid
            // adding an extra task schedule if it's unnecessary (i.e., the completion is already
            // async).
            //
            // You can bypass the 'always asynchronous' feature by putting the synchronous:true
            // flag on your component configuration when you register it.
            if (completedAsync || isSynchronousComponent) {
                // Note that notifySubscribers ignores any dependencies read within the callback.
                // See comment in loaderRegistryBehaviors.js for reasoning
                _subscribable.notifySubscribers(definition);
            }
            else {
                tasks.schedule(function () {
                    _subscribable.notifySubscribers(definition);
                });
            }
        });
        completedAsync = true;
    }
    else {
        _subscribable.subscribe(callback);
    }
}
function beginLoadingComponent(componentName, callback) {
    getFirstResultFromLoaders('getConfig', [componentName], function (config) {
        if (config) {
            // We have a config, so now load its definition
            getFirstResultFromLoaders('loadComponent', [componentName, config], function (definition) {
                callback(definition, config);
            });
        }
        else {
            // The component has no config - it's unknown to all the loaders.
            // Note that this is not an error (e.g., a module loading error) - that would abort the
            // process and this callback would not run. For this callback to run, all loaders must
            // have confirmed they don't know about this component.
            callback(null, null);
        }
    });
}
function getFirstResultFromLoaders(methodName, argsExceptCallback, callback, candidateLoaders) {
    // On the first call in the stack, start with the full set of loaders
    if (!candidateLoaders) {
        candidateLoaders = registry.loaders.slice(0); // Use a copy, because we'll be mutating this array
    }
    // Try the next candidate
    var currentCandidateLoader = candidateLoaders.shift();
    if (currentCandidateLoader) {
        var methodInstance = currentCandidateLoader[methodName];
        if (methodInstance) {
            var wasAborted = false, synchronousReturnValue = methodInstance.apply(currentCandidateLoader, argsExceptCallback.concat(function (result) {
                if (wasAborted) {
                    callback(null);
                }
                else if (result !== null) {
                    // This candidate returned a value. Use it.
                    callback(result);
                }
                else {
                    // Try the next candidate
                    getFirstResultFromLoaders(methodName, argsExceptCallback, callback, candidateLoaders);
                }
            }));
            // Currently, loaders may not return anything synchronously. This leaves open the possibility
            // that we'll extend the API to support synchronous return values in the future. It won't be
            // a breaking change, because currently no loader is allowed to return anything except undefined.
            if (synchronousReturnValue !== undefined) {
                wasAborted = true;
                // Method to suppress exceptions will remain undocumented. This is only to keep
                // KO's specs running tidily, since we can observe the loading got aborted without
                // having exceptions cluttering up the console too.
                if (!currentCandidateLoader.suppressLoaderExceptions) {
                    throw new Error('Component loaders must supply values by invoking the callback, not by returning values synchronously.');
                }
            }
        }
        else {
            // This candidate doesn't have the relevant handler. Synchronously move on to the next one.
            getFirstResultFromLoaders(methodName, argsExceptCallback, callback, candidateLoaders);
        }
    }
    else {
        // No candidates returned a value
        callback(null);
    }
}
var registry = {
    get: function (componentName, callback) {
        var cachedDefinition = getObjectOwnProperty(loadedDefinitionsCache, componentName);
        if (cachedDefinition) {
            // It's already loaded and cached. Reuse the same definition object.
            // Note that for API consistency, even cache hits complete asynchronously by default.
            // You can bypass this by putting synchronous:true on your component config.
            if (cachedDefinition.isSynchronousComponent) {
                dependencyDetection.ignore(function () {
                    callback(cachedDefinition.definition);
                });
            }
            else {
                tasks.schedule(function () { callback(cachedDefinition.definition); });
            }
        }
        else {
            // Join the loading process that is already underway, or start a new one.
            loadComponentAndNotify(componentName, callback);
        }
    },
    clearCachedDefinition: function (componentName) {
        delete loadedDefinitionsCache[componentName];
    },
    _getFirstResultFromLoaders: getFirstResultFromLoaders,
    loaders: []
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

// The default loader is responsible for two things:
// 1. Maintaining the default in-memory registry of component configuration objects
//    (i.e., the thing you're writing to when you call ko.components.register(someName, ...))
// 2. Answering requests for components by fetching configuration objects
//    from that default in-memory registry and resolving them into standard
//    component definition objects (of the form { createViewModel: ..., template: ... })
// Custom loaders may override either of these facilities, i.e.,
// 1. To supply configuration objects from some other source (e.g., conventions)
// 2. Or, to resolve configuration objects by loading viewmodels/templates via arbitrary logic.
var defaultConfigRegistry = {};
var VIEW_MODEL_FACTORY = Symbol('Knockout View Model ViewModel factory');
function register(componentName, config) {
    if (!config) {
        throw new Error('Invalid configuration for ' + componentName);
    }
    if (isRegistered(componentName)) {
        throw new Error('Component ' + componentName + ' is already registered');
    }
    var ceok = componentName.includes('-') && componentName.toLowerCase() === componentName;
    if (!config.ignoreCustomElementWarning && !ceok) {
        console.log("\n\uD83E\uDD4A  Knockout warning: components for custom elements must be lowercase and contain a dash.  To ignore this warning, add to the 'config' of .register(componentName, config):\n\n          ignoreCustomElementWarning: true\n    ");
    }
    defaultConfigRegistry[componentName] = config;
}
function isRegistered(componentName) {
    return hasOwnProperty(defaultConfigRegistry, componentName);
}
function unregister(componentName) {
    delete defaultConfigRegistry[componentName];
    registry.clearCachedDefinition(componentName);
}
var defaultLoader = {
    getConfig: function (componentName, callback) {
        var result = hasOwnProperty(defaultConfigRegistry, componentName)
            ? defaultConfigRegistry[componentName]
            : null;
        callback(result);
    },
    loadComponent: function (componentName, config, callback) {
        var errorCallback = makeErrorCallback(componentName);
        possiblyGetConfigFromAmd(errorCallback, config, function (loadedConfig) {
            resolveConfig(componentName, errorCallback, loadedConfig, callback);
        });
    },
    loadTemplate: function (componentName, templateConfig, callback) {
        resolveTemplate(makeErrorCallback(componentName), templateConfig, callback);
    },
    loadViewModel: function (componentName, viewModelConfig, callback) {
        resolveViewModel(makeErrorCallback(componentName), viewModelConfig, callback);
    }
};
var createViewModelKey = 'createViewModel';
// Takes a config object of the form { template: ..., viewModel: ... }, and asynchronously convert it
// into the standard component definition format:
//    { template: <ArrayOfDomNodes>, createViewModel: function(params, componentInfo) { ... } }.
// Since both template and viewModel may need to be resolved asynchronously, both tasks are performed
// in parallel, and the results joined when both are ready. We don't depend on any promises infrastructure,
// so this is implemented manually below.
function resolveConfig(componentName, errorCallback, config, callback) {
    var result = {}, makeCallBackWhenZero = 2, tryIssueCallback = function () {
        if (--makeCallBackWhenZero === 0) {
            callback(result);
        }
    }, templateConfig = config['template'], viewModelConfig = config['viewModel'];
    if (templateConfig) {
        possiblyGetConfigFromAmd(errorCallback, templateConfig, function (loadedConfig) {
            registry._getFirstResultFromLoaders('loadTemplate', [componentName, loadedConfig], function (resolvedTemplate) {
                result['template'] = resolvedTemplate;
                tryIssueCallback();
            });
        });
    }
    else {
        tryIssueCallback();
    }
    if (viewModelConfig) {
        possiblyGetConfigFromAmd(errorCallback, viewModelConfig, function (loadedConfig) {
            registry._getFirstResultFromLoaders('loadViewModel', [componentName, loadedConfig], function (resolvedViewModel) {
                result[createViewModelKey] = resolvedViewModel;
                tryIssueCallback();
            });
        });
    }
    else {
        tryIssueCallback();
    }
}
function resolveTemplate(errorCallback, templateConfig, callback) {
    if (typeof templateConfig === 'string') {
        // Markup - parse it
        callback(parseHtmlFragment(templateConfig));
    }
    else if (templateConfig instanceof Array) {
        // Assume already an array of DOM nodes - pass through unchanged
        callback(templateConfig);
    }
    else if (isDocumentFragment(templateConfig)) {
        // Document fragment - use its child nodes
        callback(makeArray(templateConfig.childNodes));
    }
    else if (templateConfig.element) {
        var element = templateConfig.element;
        if (isDomElement(element)) {
            // Element instance - copy its child nodes
            callback(cloneNodesFromTemplateSourceElement(element));
        }
        else if (typeof element === 'string') {
            // Element ID - find it, then copy its child nodes
            var elemInstance = document.getElementById(element);
            if (elemInstance) {
                callback(cloneNodesFromTemplateSourceElement(elemInstance));
            }
            else {
                errorCallback('Cannot find element with ID ' + element);
            }
        }
        else {
            errorCallback('Unknown element type: ' + element);
        }
    }
    else if (templateConfig.elementName) {
        // JSX in the style of babel-plugin-transform-jsx
        callback(templateConfig);
    }
    else {
        errorCallback('Unknown template value: ' + templateConfig);
    }
}
function resolveViewModel(errorCallback, viewModelConfig, callback) {
    if (viewModelConfig[VIEW_MODEL_FACTORY]) {
        callback(function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return viewModelConfig[VIEW_MODEL_FACTORY].apply(viewModelConfig, __spread(args));
        });
    }
    else if (typeof viewModelConfig === 'function') {
        // Constructor - convert to standard factory function format
        // By design, this does *not* supply componentInfo to the constructor, as the intent is that
        // componentInfo contains non-viewmodel data (e.g., the component's element) that should only
        // be used in factory functions, not viewmodel constructors.
        callback(function (params /*, componentInfo */) {
            return new viewModelConfig(params);
        });
    }
    else if (typeof viewModelConfig[createViewModelKey] === 'function') {
        // Already a factory function - use it as-is
        callback(viewModelConfig[createViewModelKey]);
    }
    else if ('instance' in viewModelConfig) {
        // Fixed object instance - promote to createViewModel format for API consistency
        var fixedInstance = viewModelConfig['instance'];
        callback(function ( /* params, componentInfo */) {
            return fixedInstance;
        });
    }
    else if ('viewModel' in viewModelConfig) {
        // Resolved AMD module whose value is of the form { viewModel: ... }
        resolveViewModel(errorCallback, viewModelConfig['viewModel'], callback);
    }
    else {
        errorCallback('Unknown viewModel value: ' + viewModelConfig);
    }
}
function cloneNodesFromTemplateSourceElement(elemInstance) {
    switch (tagNameLower(elemInstance)) {
        case 'script':
            return parseHtmlFragment(elemInstance.text);
        case 'textarea':
            return parseHtmlFragment(elemInstance.value);
        case 'template':
            // For browsers with proper <template> element support (i.e., where the .content property
            // gives a document fragment), use that document fragment.
            if (isDocumentFragment(elemInstance.content)) {
                return cloneNodes(elemInstance.content.childNodes);
            }
    }
    // Regular elements such as <div>, and <template> elements on old browsers that don't really
    // understand <template> and just treat it as a regular container
    return cloneNodes(elemInstance.childNodes);
}
function possiblyGetConfigFromAmd(errorCallback, config, callback) {
    if (typeof config.require === 'string') {
        // The config is the value of an AMD module
        if (window.amdRequire || window.require) {
            (window.amdRequire || window.require)([config.require], callback);
        }
        else {
            errorCallback('Uses require, but no AMD loader is present');
        }
    }
    else {
        callback(config);
    }
}
function makeErrorCallback(componentName) {
    return function (message) {
        throw new Error('Component \'' + componentName + '\': ' + message);
    };
}
// By default, the default loader is the only registered component loader
registry.loaders.push(defaultLoader);

var ComponentABC = /** @class */ (function (_super) {
    __extends(ComponentABC, _super);
    function ComponentABC() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Object.defineProperty(ComponentABC, "customElementName", {
        /**
       * The tag name of the custom element.  For example 'my-component'.
       * By default converts the class name from camel case to kebab case.
         * @return {string} The custom node name of this component.
         */
        get: function () {
            return this.name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComponentABC, "template", {
        /**
         * Overload this to return:
         * 1. A string of markup
         * 2. An array of DOM nodes
         * 3. A document fragment
         * 4. An AMD module (with `{require: 'some/template'}`)
         * @return {mixed} One of the accepted template types for the ComponentBinding.
         */
        get: function () {
            if ('template' in this.prototype) {
                return;
            }
            return { element: this.element };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComponentABC, "element", {
        /**
         * This is called by the default `template`.  Overload this to return:
         * 1. The element ID
         * 2. A DOM node itself
         * @return {string|HTMLElement} either the element ID or actual element.
         */
        get: function () {
            throw new Error('[ComponentABC] `element` must be overloaded.');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComponentABC, "sync", {
        /**
         * @return {bool} True if the component shall load synchronously
         */
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    /**
     * Construct a new instance of the model.  When using ComponentABC as a
     * base class, we do pass in the $element and $componentTemplateNodes.
     * @param {Object} params
     * @param {{element: HTMLElement, templateNodes: [HTMLElement]}} componentInfo
     */
    ComponentABC[VIEW_MODEL_FACTORY] = function (params, componentInfo) {
        return new this(params, componentInfo);
    };
    ComponentABC.register = function (name) {
        if (name === void 0) { name = this.customElementName; }
        var viewModel = this;
        var template = this.template;
        var synchronous = this.sync;
        register(name, { viewModel: viewModel, template: template, synchronous: synchronous });
    };
    return ComponentABC;
}(LifeCycle));

var index = {
    ComponentABC: ComponentABC,
    // -- Registry --
    get: registry.get,
    clearCachedDefinition: registry.clearCachedDefinition,
    // -- Loader --
    register: register,
    isRegistered: isRegistered,
    unregister: unregister,
    defaultLoader: defaultLoader,
    // "Privately" expose the underlying config registry for use in old-IE shim
    _allRegisteredComponents: defaultConfigRegistry,
    get loaders() { return registry.loaders; },
    set loaders(loaders) { registry.loaders = loaders; }
};

export default index;
//# sourceMappingURL=utils.component.js.map
